import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getTicketApi } from 'api/tickets';
import { RequestQueryKeysE, RequestStaleTimeE } from 'declarations/enums/react-query';
import type { RelatedTicketInfoI } from 'declarations/interfaces/tickets';

type UseRelatedTicketsResultT = {
  relatedTickets: RelatedTicketInfoI[];
  isLoadingRelatedTickets: boolean;
};

const useRelatedTickets = (
  currentTicketId: string | null,
  relatedTicketIds: string[] | undefined
): UseRelatedTicketsResultT => {
  const idsToFetch = useMemo(
    (): string[] =>
      (relatedTicketIds ?? []).filter((id): boolean => id !== currentTicketId && !!id),
    [relatedTicketIds, currentTicketId]
  );

  const queries = useQueries({
    queries: idsToFetch.map(ticketId => ({
      queryKey: [RequestQueryKeysE.FetchTicket, ticketId],
      queryFn: (): Promise<ReturnType<typeof getTicketApi> extends Promise<infer T> ? T : never> =>
        getTicketApi(ticketId),
      enabled: !!ticketId,
      staleTime: RequestStaleTimeE.Ticket,
    })),
  });

  const isLoadingRelatedTickets = queries.some((q): boolean => q.isLoading);

  const relatedTickets = useMemo((): RelatedTicketInfoI[] => {
    const results: RelatedTicketInfoI[] = [];
    for (const query of queries) {
      if (query.data) {
        const d = query.data;
        results.push({
          id: d.id,
          number: d.number,
          lineNumber: d.lineNumber,
          statusId: d.statusId,
          amount: d.amount,
          assigneeName: d.assigneeName,
          age: d.age,
        });
      }
    }
    return results;
  }, [queries]);

  return { relatedTickets, isLoadingRelatedTickets };
};

export default useRelatedTickets;
